import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, Trash2, Plus, ChevronLeft, Tag, Layers, AlertCircle, Loader2, Clock, Box } from 'lucide-react';

import styles from './PackagesForm.module.css';
import { useStore } from '../../../../../../../context/StoreContext';

const API_ORIGIN = (
    process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'
)
    .replace(/\/api\/v1(?:\/public)?\/?$/, '')
    .replace(/\/+$/, '');

const API_BASE_SERVICES = `${API_ORIGIN}/api/v1/smiify/sm/services`;
const API_BASE_INVENTORY = `${API_ORIGIN}/api/v1/smiify/sm/inventory`;

const toText = (value) => String(value ?? '').trim();

const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

const getRowsFromResponse = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.services)) return payload.services;
    if (Array.isArray(payload?.products)) return payload.products;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    if (Array.isArray(payload?.data?.services)) return payload.data.services;
    if (Array.isArray(payload?.data?.products)) return payload.data.products;
    return [];
};

const fetchCatalogRows = async (url, options, label) => {
    try {
        const response = await fetch(url, options);
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new Error(
                payload?.message || `Unable to load ${label}.`
            );
        }

        return getRowsFromResponse(payload);
    } catch (error) {
        console.error(`${label} catalog load failed:`, error);
        return [];
    }
};

const makeBlankRow = (key = '') => ({
    id: key || `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    searchKey: '',
    selectedItem: null,
    qty: 1,
    rate: 0,
    discType: '%',
    discValue: 0
});

const hydratePackageItem = (rawItem = {}, index = 0) => {
    const savedSelected = [
        rawItem.selectedItem,
        rawItem.catalogItem
    ].find((value) => value && typeof value === 'object') || null;
    const itemName = toText(
        rawItem.searchKey ||
        rawItem.itemName ||
        rawItem.name ||
        savedSelected?.name
    );

    const selectedItem = savedSelected
        ? {
            ...savedSelected,
            name: toText(savedSelected.name || itemName),
            type: toText(
                savedSelected.type ||
                rawItem.catalogType ||
                rawItem.type ||
                'SERVICE'
            ).toUpperCase(),
            category: toText(
                savedSelected.category || rawItem.category || 'General'
            ),
            currentPrice: savedSelected.currentPrice ?? toNumber(rawItem.rate)
        }
        : (itemName ? {
            id: rawItem.catalogId || `saved-${index}`,
            type: toText(rawItem.catalogType || rawItem.type || 'SERVICE').toUpperCase(),
            name: itemName,
            category: toText(rawItem.category || 'General'),
            currentPrice: toNumber(rawItem.rate)
        } : null);

    return {
        ...makeBlankRow(`saved-row-${index}`),
        ...rawItem,
        id: rawItem.id || rawItem._id || `saved-row-${index}`,
        searchKey: itemName,
        selectedItem,
        qty: rawItem.qty ?? 1,
        rate: rawItem.rate ?? selectedItem?.currentPrice ?? 0,
        discType: rawItem.discType || '%',
        discValue: rawItem.discValue ?? rawItem.discountValue ?? 0
    };
};

const getInitialRows = (data) => {
    const savedItems = Array.isArray(data?.items) ? data.items : [];
    return savedItems.length
        ? savedItems.map(hydratePackageItem)
        : [makeBlankRow()];
};

export default function PackagesForm({ initialData, onCancel, onSave }) {
    const { activeStore } = useStore();
    const storeId = activeStore?.id || activeStore?._id || '';

    const [dbItems, setDbItems] = useState([]);
    const [hasServices, setHasServices] = useState(true);
    const [isLoadingData, setIsLoadingData] = useState(true);

    const [formData, setFormData] = useState({
        name: initialData?.name || '', 
        validity: initialData?.validity ?? '3', 
        validityType: initialData?.validityType || 'Months', 
        qtyLimit: initialData?.qtyLimit || ''
    });

    const [items, setItems] = useState(() => getInitialRows(initialData));

    const [overallDiscValue, setOverallDiscValue] = useState(initialData?.packageDiscValue ?? 0);
    const [overallDiscType, setOverallDiscType] = useState(initialData?.packageDiscType || '%');
    
    const [activeRowId, setActiveRowId] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        setFormData({
            name: initialData?.name || '',
            validity: initialData?.validity ?? '3',
            validityType: initialData?.validityType || 'Months',
            qtyLimit: initialData?.qtyLimit || ''
        });
        setItems(getInitialRows(initialData));
        setOverallDiscValue(initialData?.packageDiscValue ?? 0);
        setOverallDiscType(initialData?.packageDiscType || '%');
        setActiveRowId(null);
    }, [initialData]);

    useEffect(() => {
        const handleClickOutside = (e) => { 
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setActiveRowId(null); 
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // 🚀 DYNAMIC FETCH ENGINE (COMBO CALCULATION)
    useEffect(() => {
        const loadCatalog = async () => {
            if (!storeId || storeId === 'global_hq') {
                setDbItems([]);
                setHasServices(false);
                setIsLoadingData(false);
                return;
            }
            
            setIsLoadingData(true);
            try {
                const token = localStorage.getItem('smiify_token');
                const headers = {
                    Authorization: `Bearer ${token}`,
                    'x-store-id': storeId
                };

                const catalogList = [];
                const [serviceRows, inventoryRows] = await Promise.all([
                    fetchCatalogRows(
                        `${API_BASE_SERVICES}?storeId=${encodeURIComponent(storeId)}`,
                        { headers },
                        'Services'
                    ),
                    fetchCatalogRows(
                        `${API_BASE_INVENTORY}?storeId=${encodeURIComponent(storeId)}&usage=pos`,
                        { headers },
                        'Inventory'
                    )
                ]);

                // 1. Active services and their add-on combos
                serviceRows.forEach((service) => {
                    const serviceName = toText(
                        service.name || service.serviceName
                    );

                    if (!serviceName) return;

                    const baseCurrent = toNumber(
                        service.effectivePrice ??
                        service.finalPrice ??
                        service.price ??
                        service.rate
                    );
                    const baseOriginal = toNumber(
                        service.price ?? baseCurrent
                    );
                    const serviceId = service._id || service.id || serviceName;

                    catalogList.push({
                        id: String(serviceId),
                        type: 'SERVICE',
                        name: serviceName,
                        category: toText(service.category || 'General'),
                        currentPrice: baseCurrent,
                        originalPrice: baseOriginal > baseCurrent ? baseOriginal : null
                    });

                    // Add-on combo rows
                    if (Array.isArray(service.addons)) {
                        service.addons.forEach((addon) => {
                            const addonName = toText(addon.name || addon.addonName);
                            if (!addonName) return;

                            const addonCurrent = toNumber(
                                addon.effectivePrice ??
                                addon.finalPrice ??
                                addon.price
                            );
                            const addonOriginal = toNumber(
                                addon.price ?? addonCurrent
                            );
                            const comboCurrent = baseCurrent + addonCurrent;
                            const comboOriginal = baseOriginal + addonOriginal;

                            catalogList.push({
                                id: `${serviceId}_addon_${addon._id || addon.id || addonName}`,
                                type: 'ADD-ON',
                                name: `${addonName} - ${serviceName}`,
                                category: toText(service.category || 'General'),
                                currentPrice: comboCurrent,
                                originalPrice: comboOriginal > comboCurrent ? comboOriginal : null
                            });
                        });
                    }
                });

                // 2. Only RETAIL and HYBRID inventory products
                inventoryRows.forEach((product) => {
                    const basicInfo = product.basicInfo || {};
                    const pricing = product.pricing || {};
                    const usageType = toText(product.usageType).toUpperCase();

                    if (usageType && !['RETAIL', 'HYBRID'].includes(usageType)) {
                        return;
                    }

                    const productName = toText(
                        basicInfo.productName || product.productName || product.name
                    );
                    if (!productName) return;

                    const currentPrice = toNumber(
                        pricing.sellingPrice ??
                        pricing.sell ??
                        product.sellingPrice ??
                        product.rate
                    );
                    const mrp = toNumber(
                        pricing.mrp ?? product.mrp ?? currentPrice
                    );

                    catalogList.push({
                        id: String(product._id || product.id || basicInfo.sku || productName),
                        type: 'PRODUCT',
                        name: productName,
                        category: toText(basicInfo.category || 'Retail'),
                        currentPrice,
                        originalPrice: mrp > currentPrice ? mrp : null
                    });
                });

                setHasServices(catalogList.length > 0);
                setDbItems(catalogList);

            } catch (error) {
                console.error("Failed to fetch catalog data:", error);
                setDbItems([]);
                setHasServices(false);
            } finally {
                setIsLoadingData(false);
            }
        };

        loadCatalog();
    }, [storeId]);

    // 🚀 LIVE CALCULATIONS ENGINE
    const gridData = useMemo(() => {
        let grossAmount = 0, totalItemDiscounts = 0, totalQty = 0;
        
        const processedItems = items.map(item => {
            const qty = Number(item.qty) || 1;
            const rate = Number(item.rate) || 0;
            const lineWorth = rate * qty;
            
            let lineDisc = 0;
            const dv = Number(item.discValue) || 0;
            if (item.discType === '₹') lineDisc = dv;
            else if (item.discType === '%') lineDisc = lineWorth * (dv / 100);
            
            const amount = Math.max(0, lineWorth - lineDisc);
            
            grossAmount += lineWorth;
            totalItemDiscounts += lineDisc;
            totalQty += qty;

            return { ...item, amount };
        });

        const taxableAmount = Math.max(0, grossAmount - totalItemDiscounts);
        
        let packageDisc = 0;
        const odv = Number(overallDiscValue) || 0;
        if(overallDiscType === '₹') packageDisc = odv;
        else if(overallDiscType === '%') packageDisc = taxableAmount * (odv / 100);

        const netPayable = Math.max(0, taxableAmount - packageDisc);

        return { items: processedItems, grossAmount, totalItemDiscounts, totalQty, packageDisc, netPayable };
    }, [items, overallDiscValue, overallDiscType]);

    const addRow = () => {
        setItems((current) => [...current, makeBlankRow()]);
    };

    const removeRow = (id) => {
        setItems((current) => (
            current.length > 1
                ? current.filter((item) => item.id !== id)
                : current
        ));
    };

    const handleSelectInventory = (rowId, invItem) => {
        setItems((current) => {
            const rowIndex = current.findIndex((item) => item.id === rowId);
            if (rowIndex === -1) return current;

            const nextItems = current.map((item) => (
                item.id === rowId
                    ? {
                        ...item,
                        selectedItem: invItem,
                        searchKey: invItem.name,
                        rate: invItem.currentPrice
                    }
                    : item
            ));

            // Last row select hote hi ek fresh blank row automatically add hogi.
            if (rowIndex === current.length - 1) {
                nextItems.push(makeBlankRow());
            }

            return nextItems;
        });

        setActiveRowId(null);
    };

    const updateRow = (id, field, value) => {
        setItems((current) => current.map((item) => (
            item.id === id
                ? { ...item, [field]: value }
                : item
        )));
    };

    const handleSave = () => {
        if (!formData.name.trim()) return alert("Package name is required!");
        
        const validItems = gridData.items.filter(
            (item) => toText(item.searchKey).trim() !== ''
        );
        if(validItems.length === 0) return alert("Add at least one item to the package.");

        onSave({
            packageId: initialData?.id,
            ...formData,
            items: validItems,
            packageDiscType: overallDiscType,
            packageDiscValue: overallDiscValue,
            grossAmount: gridData.grossAmount,
            totalItemDiscounts: gridData.totalItemDiscounts,
            packageDisc: gridData.packageDisc,
            netPayable: gridData.netPayable
        });
    };

    if (isLoadingData) {
        return (
            <div className={styles.workspace} style={{ alignItems: 'center', justifyContent: 'center' }}>
                 <Loader2 size={32} className={styles.spinner} color="var(--h-color-primary-600)"/>
                 <p className={styles.loadingText}>Fetching pricing catalog...</p>
            </div>
        );
    }

    if (!hasServices) {
        return (
            <div className={styles.workspace} style={{ alignItems: 'center', justifyContent: 'center' }}>
                <div className={styles.noServiceContainer}>
                    <div className={styles.warningIcon}><AlertCircle size={32} /></div>
                    <h3>No Services Found</h3>
                    <p>You need to have at least one active service or product in your catalog to build a package.</p>
                    <button className={styles.btnDraft} onClick={onCancel} style={{width: '100%'}}>Go Back</button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.workspace}>
            <header className={styles.topBar}>
                <div className={styles.topLeft}>
                    <button className={styles.backBtn} onClick={onCancel}><ChevronLeft size={16}/></button>
                    <div className={styles.pageTitles}>
                        <span className={styles.breadcrumb}>Pricelist / Packages</span>
                        <h2 className={styles.pageTitle}>{initialData ? 'Edit Package' : 'Create Custom Package'}</h2>
                    </div>
                </div>
                <div className={styles.topRight}>
                    <button className={styles.btnDraft} onClick={onCancel}>Cancel</button>
                    <button className={styles.btnSave} onClick={handleSave}>Save Package</button>
                </div>
            </header>

            <div className={styles.mainSplit}>
                <div className={styles.leftPanel}>
                    {/* 🔥 1. PACKAGE IDENTITY (Perfect Single Row Alignment) */}
                    <div className={styles.masterCard}>
                        <div className={styles.cardHeader}><Layers size={13} color="var(--h-color-primary-600)"/> Package Identity</div>
                        <div className={styles.cardBodyRow}>
                            <div className={styles.formGroup} style={{flex: 2}}>
                                <label>Package Name <span className={styles.req}>*</span></label>
                                <input type="text" className={styles.standardInput} placeholder="e.g. Bridal Pre-Wedding Glow" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}/>
                            </div>
                            <div className={styles.formGroup} style={{flex: 1.2}}>
                                <label>Validity <span className={styles.req}>*</span></label>
                                <div className={styles.inputGroupSplit}>
                                    <input type="number" className={styles.splitLeft} placeholder="3" value={formData.validity} onChange={e => setFormData({...formData, validity: e.target.value})}/>
                                    <select className={styles.splitRight} value={formData.validityType} onChange={e => setFormData({...formData, validityType: e.target.value})}>
                                        <option value="Months">Months</option><option value="Years">Years</option>
                                    </select>
                                </div>
                            </div>
                            <div className={styles.formGroup} style={{flex: 1}}>
                                <label>Qty Limit</label>
                                <input type="number" className={styles.standardInput} placeholder="∞ Optional" value={formData.qtyLimit} onChange={e => setFormData({...formData, qtyLimit: e.target.value})}/>
                            </div>
                        </div>
                    </div>

                    {/* 🔥 2. NATIVE TABLE GRID */}
                    <div className={styles.gridCard}>
                        <div className={styles.tableWrapper}>
                            <table className={styles.dataTable}>
                                <colgroup>
                                    <col width="40px" />
                                    <col width="auto" />
                                    <col width="70px" />
                                    <col width="90px" />
                                    <col width="120px" />
                                    <col width="100px" />
                                    <col width="45px" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className={styles.center}>#</th>
                                        <th>Item / Service</th>
                                        <th className={styles.center}>Qty</th>
                                        <th className={styles.right}>Rate (₹)</th>
                                        <th>Discount</th>
                                        <th className={styles.right}>Amount (₹)</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gridData.items.map((item, index) => (
                                        <tr key={item.id} className={activeRowId === item.id ? styles.activeRow : ''}>
                                            <td>
                                                <div className={`${styles.cellText} ${styles.center}`} style={{color: 'var(--h-color-neutral-400)'}}>
                                                    {String(index + 1).padStart(2, '0')}
                                                </div>
                                            </td>
                                            <td style={{position: 'relative'}} ref={activeRowId === item.id ? dropdownRef : null}>
                                                {!item.selectedItem ? (
                                                    <div className={styles.searchWrapper} onClick={() => setActiveRowId(item.id)}>
                                                        <Search size={13} className={styles.searchIcon}/>
                                                        <input 
                                                            type="text" className={styles.searchInput} 
                                                            placeholder="Search catalog..." 
                                                            value={item.searchKey} onChange={e => updateRow(item.id, 'searchKey', e.target.value)}
                                                            onFocus={() => setActiveRowId(item.id)}
                                                        />
                                                        <ChevronDown size={13} className={styles.searchIcon}/>
                                                    </div>
                                                ) : (
                                                    <div className={styles.selectedItemBox} onClick={() => { updateRow(item.id, 'selectedItem', null); updateRow(item.id, 'searchKey', ''); setActiveRowId(item.id); }}>
                                                        <span className={styles.selectedName}>{item.selectedItem.name}</span>
                                                        <div className={styles.itemMeta}>
                                                       
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* 🔥 ZOMATO-STYLE PREMIUM DROPDOWN */}
                                                {activeRowId === item.id && (
                                                    <div className={styles.dropdownBox}>
                                                        {dbItems.filter(inv => inv.name.toLowerCase().includes(item.searchKey.toLowerCase())).length === 0 ? (
                                                            <div className={styles.dropdownEmpty}>No items found in catalog.</div>
                                                        ) : (
                                                            dbItems.filter(inv => inv.name.toLowerCase().includes(item.searchKey.toLowerCase())).map(inv => {
                                                                let TagIcon = Clock;
                                                                let tagClass = styles.tagService;
                                                                if (inv.type === 'ADD-ON') { TagIcon = Plus; tagClass = styles.tagAddon; }
                                                                if (inv.type === 'PRODUCT') { TagIcon = Box; tagClass = styles.tagProduct; }

                                                                return (
                                                                    <div key={inv.id} className={styles.dropdownItem} onClick={() => handleSelectInventory(item.id, inv)}>
                                                                        <div className={styles.itemLeft}>
                                                                            <span className={styles.itemName}>{inv.name}</span>
                                                                            <div className={styles.itemMeta}>
                                                                                <span className={`${styles.itemTag} ${tagClass}`}>
                                                                                    <TagIcon size={9} /> {inv.type}
                                                                                </span>
                                                                                <span className={styles.itemCategory}>{inv.category}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className={styles.itemRight}>
                                                                            <span className={styles.itemCurrentPrice}>₹{inv.currentPrice}</span>
                                                                            {inv.originalPrice && <span className={styles.itemOriginalPrice}>₹{inv.originalPrice}</span>}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td><input type="number" className={`${styles.seamlessInput} ${styles.center}`} value={item.qty} onChange={e => updateRow(item.id, 'qty', e.target.value)} min="1"/></td>
                                            <td><input type="number" className={`${styles.seamlessInput} ${styles.right}`} value={item.rate} onChange={e => updateRow(item.id, 'rate', e.target.value)} min="0"/></td>
                                            <td>
                                                <div className={styles.discountComposite}>
                                                    <input type="number" className={styles.discountInput} value={item.discValue} onChange={e => updateRow(item.id, 'discValue', e.target.value)} min="0"/>
                                                    <select className={styles.discountSelect} value={item.discType} onChange={e => updateRow(item.id, 'discType', e.target.value)}>
                                                        <option value="%">%</option><option value="₹">₹</option>
                                                    </select>
                                                </div>
                                            </td>
                                            <td><div className={`${styles.cellText} ${styles.right} ${styles.amount}`}>₹{item.amount.toFixed(2)}</div></td>
                                            <td style={{textAlign: 'center'}}><button className={styles.deleteBtn} onClick={() => removeRow(item.id)} title="Remove row"><Trash2 size={14}/></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td></td>
                                        <td style={{textAlign: 'right'}}><span className={styles.footerLabel}>Totals</span></td>
                                        <td style={{textAlign: 'center'}}><span className={styles.footerVal}>{gridData.totalQty}</span></td>
                                        <td style={{textAlign: 'right'}}><span className={styles.footerVal}>₹{gridData.grossAmount.toFixed(2)}</span></td>
                                        <td style={{textAlign: 'right'}}><span className={styles.footerVal} style={{color: 'var(--h-color-success-600)'}}>- ₹{gridData.totalItemDiscounts.toFixed(2)}</span></td>
                                        <td style={{textAlign: 'right'}}><span className={styles.footerAmount}>₹{(gridData.grossAmount - gridData.totalItemDiscounts).toFixed(2)}</span></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        <div className={styles.addLineWrap}>
                            <button className={styles.addLineBtn} onClick={addRow}><Plus size={13}/> Add blank row</button>
                        </div>
                    </div>
                </div>

                {/* ======================= RIGHT PANEL (POS BILL SUMMARY) ======================= */}
                <div className={styles.rightPanel}>
                    <div className={styles.receiptContainer}>
                        <h3 className={styles.summaryTitle}>Pricing Summary</h3>
                        
                        <div className={styles.receiptBody}>
                            <div className={styles.summaryRow}>
                                <span>Gross Value</span>
                                <span className={styles.boldText}>₹{gridData.grossAmount.toFixed(2)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.greenText}>Item Discounts</span>
                                <span className={styles.greenText}>- ₹{gridData.totalItemDiscounts.toFixed(2)}</span>
                            </div>
                            <div className={styles.summaryDivider}></div>
                            <div className={styles.summaryRow}>
                                <span>Package Total Worth</span>
                                <span className={styles.boldText}>₹{(gridData.grossAmount - gridData.totalItemDiscounts).toFixed(2)}</span>
                            </div>

                            <div className={styles.applyDiscBox}>
                                <label>Apply Flat Package Discount</label>
                                <div className={styles.discInputGroup}>
                                    <input type="number" placeholder="0" value={overallDiscValue} onChange={e => setOverallDiscValue(e.target.value)} min="0"/>
                                    <select value={overallDiscType} onChange={e => setOverallDiscType(e.target.value)}>
                                        <option value="%">%</option><option value="₹">₹</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div className={styles.summaryRow}>
                                <span>Flat Discount</span>
                                <span className={styles.greenText}>- ₹{gridData.packageDisc.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className={styles.netPayableBox}>
                            <span className={styles.netPayableLabel}>Final Selling Price</span>
                            <span className={styles.netPayableVal}>₹{gridData.netPayable.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}